import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfiguracoesService } from './services';

import Layout from './components/Layout/Layout';
import Login from './pages/Login/Login';
import PlaceholderPage from './components/ui/PlaceholderPage';
import NotFound from './components/ui/NotFound';
import StatusTickets from './pages/StatusTickets/StatusTickets';
import Priorities from './pages/Priorities/Priorities';
import Departments from './pages/Departments/Departments';
import Units from './pages/Units/Units';
import Subjects from './pages/Subjects/Subjects';
import Configurations from './pages/Configurations/Configurations';
import LoginAttempts from './pages/LoginAttempts/LoginAttempts';
import ChangelogView from './pages/Changelog/ChangelogView';
import ChangelogManagement from './pages/Changelog/ChangelogManagement';
import Users from './pages/Users/Users';
import SlaConfig from './pages/SlaConfig/SlaConfig';
import NewUser from './pages/Users/NewUser';
import EditUser from './pages/Users/EditUser';
import UserProfile from './pages/Users/UserProfile';
import UserLogs from './pages/UserLogs/UserLogs';
import Gallery from './pages/Gallery/Gallery';
import Tags from './pages/Tags/Tags';
import Templates from './pages/Templates/Templates';

import Tickets from './pages/Tickets/Tickets';
import NewTicket from './pages/Tickets/NewTicket';
import TicketDetail from './pages/Tickets/TicketDetail';
import MyTickets from './pages/Tickets/MyTickets';
import Favoritos from './pages/Favoritos/Favoritos';
import BaseConhecimentoView from './pages/BaseConhecimento/BaseConhecimento';
import GerenciarBaseConhecimento from './pages/BaseConhecimento/GerenciarBaseConhecimento';
import ArtigoDetail from './pages/BaseConhecimento/ArtigoDetail';
import NovoArtigo from './pages/BaseConhecimento/NovoArtigo';
import EditarArtigo from './pages/BaseConhecimento/EditarArtigo';
import GerenciarCategorias from './pages/BaseConhecimento/GerenciarCategorias';
import ListaNotificacoes from './pages/Notificacoes/ListaNotificacoes';
import NotificationSetup from './pages/Notificacoes/NotificationSetup';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAStatus from './components/PWAStatus';
import { useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/ui/LoadingSpinner';
const HomeRedirect: React.FC = () => {
  const { usuario } = useAuth();
  
  if (usuario?.nivel === 'usuario') {
    return <Navigate to="/tickets/meus-tickets" replace />;
  }
  
  return <Tickets />;
};

const PWAPromptWrapper: React.FC<{ showAlways?: boolean }> = ({ showAlways = false }) => {
  const [mounted, setMounted] = React.useState(false);
  
  React.useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 200);
    return () => clearTimeout(timer);
  }, []);
  
  if (!mounted) return null;
  
  return <PWAInstallPrompt showAlways={showAlways} />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 dark:from-gray-900 via-blue-50 dark:via-gray-900 to-indigo-50 dark:to-gray-800 flex items-center justify-center">
        <LoadingSpinner 
          size="xl" 
          variant="sparkles" 
          text="Carregando HelpDesk..." 
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <PWAPromptWrapper showAlways={true} />
      </div>
    );
  }

  return (
    <div>
      <Layout>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/tickets/novo" element={<NewTicket />} />
          <Route path="/tickets/meus-tickets" element={<MyTickets />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/favoritos" element={<Favoritos />} />
          <Route path="/usuarios" element={<Users />} />
          <Route path="/usuarios/adicionar" element={<NewUser />} />
          <Route path="/usuarios/editar/:id" element={<EditUser />} />
          <Route path="/usuarios/perfil" element={<UserProfile />} />
          <Route path="/departamentos" element={<Departments />} />
          <Route path="/unidades" element={<Units />} />
          <Route path="/assuntos" element={<Subjects />} />
          <Route path="/tags" element={<Tags />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/prioridades" element={<Priorities />} />
          <Route path="/status-ticket" element={<StatusTickets />} />
          <Route path="/alertas" element={<PlaceholderPage title="Alertas" description="Sistema de notificações e alertas." />} />
          <Route path="/configuracoes" element={<Configurations />} />
          <Route path="/sla-config" element={<SlaConfig />} />
          <Route path="/changelogs" element={<ChangelogView />} />
          <Route path="/changelogs/gerenciar" element={<ChangelogManagement />} />
          <Route path="/galeria" element={<Gallery />} />
          <Route path="/tentativas-login" element={<LoginAttempts />} />
          <Route path="/logs-usuarios" element={<UserLogs />} />
          <Route path="/notificacoes" element={<ListaNotificacoes />} />
          <Route path="/notificacoes/configurar" element={<NotificationSetup />} />
          <Route path="/base-conhecimento/*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
      <PWAPromptWrapper />
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    const setDynamicTitle = async () => {
      try {
        const configuracao = await ConfiguracoesService.getConfiguracaoPorNome('nome_site');
        const nomeSite = configuracao?.valor || 'HelpDesk';
        document.title = nomeSite;
      } catch (error) {
        console.warn('Erro ao carregar configuração do nome do site:', error);
        document.title = 'HelpDesk';
      }
    };

    setDynamicTitle();
  }, []);

  return (
    <AppRoutes />
  );
};

export default App;
